<template>
  <div class="plot-detail">
    <div class="plot-detail__header">
      <h3 class="plot-detail__title">{{ plot?.name }}</h3>
      <button class="plot-detail__close" @click="$emit('close')" aria-label="Cerrar">
        <span class="mdi mdi-close"></span>
      </button>
    </div>

    <div v-if="loading" class="plot-detail__loading">Cargando...</div>

    <template v-else-if="plot">
      <div class="plot-detail__status">
        <span class="plot-detail__status-dot" :style="{ background: STATUS_COLORS[plot.status] }"></span>
        {{ STATUS_LABELS[plot.status] }}
        <span v-if="plot.status === 'OCCUPIED' && plot.tickets?.length" class="plot-detail__badge">
          <span class="mdi mdi-wrench-outline"></span> Con mantenimiento activo ({{ plot.tickets.length }})
        </span>
      </div>

      <p v-if="plot.autoMaintenance" class="plot-detail__hint">
        En mantenimiento por tickets abiertos. Cuando se resuelvan, la nave vuelve a Vacante.
      </p>

      <p v-else-if="plot.status === 'OCCUPIED'" class="plot-detail__hint">
        Ocupada por su contrato activo. Su estado cambia solo cuando el contrato termina.
      </p>

      <div v-else class="plot-detail__status-edit">
        <span class="plot-detail__label">Estado de la nave</span>
        <div class="plot-detail__status-options">
          <button
            v-for="opt in MANUAL_OPTIONS"
            :key="opt.value"
            type="button"
            class="plot-detail__status-opt"
            :class="{ 'plot-detail__status-opt--active': draftStatus === opt.value }"
            @click="draftStatus = opt.value"
          >{{ opt.label }}</button>
        </div>
        <input
          v-if="draftStatus !== 'VACANT'"
          v-model.trim="draftNote"
          type="text"
          maxlength="300"
          class="plot-detail__input"
          :placeholder="draftStatus === 'RESERVED' ? 'Con quién se negocia (opcional)' : 'Motivo, ej. remodelación (opcional)'"
        />
        <p v-if="plot.statusNote && plot.status !== 'VACANT' && !dirty" class="plot-detail__note">Nota: {{ plot.statusNote }}</p>
        <p v-if="statusError" class="plot-detail__error">{{ statusError }}</p>
        <button v-if="dirty" type="button" class="plot-detail__save" :disabled="savingStatus" @click="saveStatus">
          {{ savingStatus ? 'Guardando...' : 'Guardar estado' }}
        </button>
      </div>

      <div class="plot-detail__row" v-if="plot.areaM2">
        <span class="plot-detail__label">Área</span>
        <span class="plot-detail__value">{{ plot.areaM2 }} m²</span>
      </div>

      <p v-if="!activeContract" class="plot-detail__hint">
        Sin contrato activo: no se cobra a ningún inquilino. Los costos de un ticket de esta nave se registran como gasto del parque y aparecen en Finanzas. Agrégalos desde el ticket (sección Costos).
      </p>

      <div class="plot-detail__row" v-if="plot.currentTenant">
        <span class="plot-detail__label">Inquilino</span>
        <span class="plot-detail__value">{{ plot.currentTenant.name }}</span>
      </div>

      <template v-if="activeContract">
        <div class="plot-detail__row">
          <span class="plot-detail__label">Renta mensual</span>
          <span class="plot-detail__value">{{ formatCurrency(activeContract.monthlyRent) }}</span>
        </div>
        <div class="plot-detail__row">
          <span class="plot-detail__label">Vence</span>
          <span class="plot-detail__value">{{ formatDate(activeContract.endDate) }}</span>
        </div>
      </template>

      <div v-if="plot.tickets?.length" class="plot-detail__tickets">
        <span class="plot-detail__label">Tickets abiertos ({{ plot.tickets.length }})</span>
        <ul class="plot-detail__ticket-list">
          <li
            v-for="t in plot.tickets"
            :key="t.id"
            class="plot-detail__ticket-row"
            @click="$emit('open-ticket', t.id)"
          >
            <span class="plot-detail__ticket-priority-dot" :style="{ background: TICKET_PRIORITY_COLORS[t.priority] }"></span>
            <span class="plot-detail__ticket-title">{{ t.title }}</span>
            <span class="plot-detail__ticket-status">{{ TICKET_STATUS_LABELS[t.status] || t.status }}</span>
          </li>
        </ul>
      </div>

      <PlotExpensesBlock :plot="plot" />

      <div class="plot-detail__danger">
        <button v-if="!confirmingDelete" type="button" class="plot-detail__delete" :disabled="checkingDelete" @click="startDelete">
          <span class="mdi mdi-trash-can-outline"></span> {{ checkingDelete ? 'Revisando...' : 'Eliminar nave' }}
        </button>
        <div v-else class="plot-detail__confirm">
          <template v-if="deleteInfo && !deleteInfo.canDelete">
            <p class="plot-detail__confirm-title">No se puede eliminar todavía</p>
            <ul class="plot-detail__blockers">
              <li v-for="b in deleteInfo.blockers" :key="b">{{ b }}</li>
            </ul>
            <button type="button" class="plot-detail__cancel" @click="cancelDelete">Entendido</button>
          </template>
          <template v-else-if="deleteInfo">
            <p class="plot-detail__confirm-title">¿Eliminar {{ deleteInfo.name }}?</p>
            <p class="plot-detail__confirm-text">
              <template v-if="deleteInfo.mode === 'ARCHIVE'">
                Esta nave tiene historial (contratos, tickets o cobros). Saldrá del mapa, las listas y los conteos, pero su historial se conserva en Finanzas, Cobranza y Contratos.
              </template>
              <template v-else>
                Esta nave no tiene historial, se borrará por completo.
              </template>
            </p>
            <p v-if="deleteError" class="plot-detail__error">{{ deleteError }}</p>
            <div class="plot-detail__confirm-actions">
              <button type="button" class="plot-detail__cancel" :disabled="deleting" @click="cancelDelete">Cancelar</button>
              <button type="button" class="plot-detail__delete-confirm" :disabled="deleting" @click="confirmDelete">{{ deleting ? 'Eliminando...' : 'Sí, eliminar' }}</button>
            </div>
          </template>
          <p v-else-if="deleteError" class="plot-detail__error">{{ deleteError }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { updatePlotStatus, getPlotDeleteCheck, deletePlot } from '@/services/plots'
import PlotExpensesBlock from './PlotExpensesBlock.vue'

const props = defineProps({
  plot: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'open-ticket', 'status-changed', 'deleted'])

const STATUS_COLORS = {
  OCCUPIED: 'var(--status-occupied)',
  VACANT: 'var(--status-vacant)',
  MAINTENANCE: 'var(--status-maintenance)',
  RESERVED: 'var(--status-reserved)',
}

const STATUS_LABELS = {
  OCCUPIED: 'Ocupada',
  VACANT: 'Vacante',
  MAINTENANCE: 'Mantenimiento',
  RESERVED: 'En negociación',
}

const TICKET_PRIORITY_COLORS = {
  CRITICAL: '#e84545',
  HIGH: '#f97316',
  MEDIUM: '#f0a500',
  LOW: '#94a3b8',
}

const TICKET_STATUS_LABELS = {
  OPEN: 'Abierto',
  IN_PROGRESS: 'En proceso',
  WAITING: 'En espera',
}

const MANUAL_OPTIONS = [
  { value: 'VACANT', label: 'Vacante' },
  { value: 'RESERVED', label: 'En negociación' },
  { value: 'MAINTENANCE', label: 'En mantenimiento' },
]

const draftStatus = ref('VACANT')
const draftNote = ref('')
const savingStatus = ref(false)
const statusError = ref('')

watch(
  () => props.plot,
  (p) => {
    draftStatus.value = p && p.status !== 'OCCUPIED' ? p.status : 'VACANT'
    draftNote.value = p?.statusNote || ''
    statusError.value = ''
  },
  { immediate: true }
)

const dirty = computed(() => !!props.plot && (draftStatus.value !== props.plot.status || (draftStatus.value !== 'VACANT' && draftNote.value !== (props.plot.statusNote || ''))))

async function saveStatus() {
  savingStatus.value = true
  statusError.value = ''
  try {
    const updated = await updatePlotStatus(props.plot.id, { status: draftStatus.value, note: draftNote.value })
    emit('status-changed', updated)
  } catch (err) {
    statusError.value = err.message
  } finally {
    savingStatus.value = false
  }
}

const confirmingDelete = ref(false)
const checkingDelete = ref(false)
const deleting = ref(false)
const deleteInfo = ref(null)
const deleteError = ref('')

watch(() => props.plot?.id, () => cancelDelete())

async function startDelete() {
  checkingDelete.value = true
  deleteError.value = ''
  deleteInfo.value = null
  try {
    deleteInfo.value = await getPlotDeleteCheck(props.plot.id)
  } catch (err) {
    deleteError.value = err.message
  } finally {
    checkingDelete.value = false
    confirmingDelete.value = true
  }
}

function cancelDelete() {
  confirmingDelete.value = false
  deleteInfo.value = null
  deleteError.value = ''
}

async function confirmDelete() {
  deleting.value = true
  deleteError.value = ''
  try {
    await deletePlot(props.plot.id)
    emit('deleted')
  } catch (err) {
    deleteError.value = err.message
  } finally {
    deleting.value = false
  }
}

const activeContract = computed(() => props.plot?.contracts?.[0] || null)

function formatCurrency(amount) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount)
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
}
</script>

<style scoped>
.plot-detail__danger { margin-top: 0.5rem; padding-top: 0.75rem; border-top: 1px solid var(--border-subtle); }
.plot-detail__delete { display: inline-flex; align-items: center; gap: 0.35rem; background: none; border: none; color: #dc2626; font-family: var(--font-body); font-size: 12px; cursor: pointer; padding: 0; }
.plot-detail__delete:disabled { opacity: 0.5; cursor: default; }
.plot-detail__confirm { display: flex; flex-direction: column; gap: 0.5rem; font-family: var(--font-body); }
.plot-detail__confirm-title { margin: 0; font-size: 13px; font-weight: 500; color: var(--text-content); }
.plot-detail__confirm-text { margin: 0; font-size: 12px; color: #64748b; line-height: 1.45; }
.plot-detail__blockers { margin: 0; padding-left: 1.1rem; font-size: 12px; color: #64748b; line-height: 1.45; }
.plot-detail__confirm-actions { display: flex; gap: 0.5rem; }
.plot-detail__cancel { align-self: flex-start; height: 30px; padding: 0 0.8rem; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; font-size: 12px; cursor: pointer; }
.plot-detail__delete-confirm { height: 30px; padding: 0 0.8rem; border-radius: 8px; border: none; background: #dc2626; color: #fff; font-size: 12px; cursor: pointer; }
.plot-detail__delete-confirm:disabled, .plot-detail__cancel:disabled { opacity: 0.5; cursor: default; }

.plot-detail {
  background: var(--bg-surface-1);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
}

.plot-detail > * { flex-shrink: 0; }
.plot-detail,
.plot-detail__ticket-list { scrollbar-width: thin; scrollbar-color: var(--border-active) transparent; }
.plot-detail::-webkit-scrollbar,
.plot-detail__ticket-list::-webkit-scrollbar { width: 6px; }
.plot-detail::-webkit-scrollbar-thumb,
.plot-detail__ticket-list::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.plot-detail::-webkit-scrollbar-track,
.plot-detail__ticket-list::-webkit-scrollbar-track { background: transparent; }

.plot-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.plot-detail__title {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-content);
  font-family: var(--font-body);
}

.plot-detail__close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1.1rem;
}

.plot-detail__close:hover {
  color: var(--text-content);
}

.plot-detail__badge { margin-left: auto; display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.7rem; color: #b45309; background: #fef3c7; border-radius: 6px; padding: 0.1rem 0.45rem; }
.plot-detail__hint { margin: 0; font-size: 0.75rem; line-height: 1.4; color: var(--text-secondary); }
.plot-detail__status-edit { display: flex; flex-direction: column; gap: 0.45rem; }
.plot-detail__status-options { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.plot-detail__status-opt { background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 0.25rem 0.65rem; font-size: 0.75rem; color: #64748b; cursor: pointer; }
.plot-detail__status-opt--active { background: #3b82f6; border-color: #3b82f6; color: #fff; }
.plot-detail__input { font-family: var(--font-body); font-size: 0.8rem; border: 1px solid var(--border-active); border-radius: 8px; padding: 0.4rem 0.6rem; }
.plot-detail__input:focus { outline: none; border-color: #3b82f6; }
.plot-detail__note { margin: 0; font-size: 0.75rem; color: var(--text-secondary); }
.plot-detail__error { margin: 0; font-size: 0.75rem; color: #dc2626; }
.plot-detail__save { align-self: flex-start; background: #3b82f6; color: #fff; border: none; border-radius: 8px; padding: 0.4rem 0.9rem; font-size: 0.8rem; cursor: pointer; }
.plot-detail__save:disabled { opacity: 0.6; cursor: default; }

.plot-detail__loading {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.plot-detail__status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-muted);
}

.plot-detail__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.plot-detail__row {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
}

.plot-detail__label {
  color: var(--text-muted);
}

.plot-detail__value {
  color: var(--text-content);
  font-family: var(--font-mono);
  font-weight: 500;
}

.plot-detail__tickets {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.plot-detail__ticket-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  max-height: 200px;
  overflow-y: auto;
}

.plot-detail__ticket-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: var(--text-content);
  cursor: pointer;
  padding: 0.3rem 0.4rem;
  border-radius: 6px;
  transition: background 0.15s ease;
}

.plot-detail__ticket-row:hover {
  background: #f1f5f9;
}

.plot-detail__ticket-priority-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.plot-detail__ticket-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plot-detail__ticket-status {
  font-size: 0.68rem;
  color: var(--text-muted);
  flex-shrink: 0;
}
</style>